import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { InlineOption } from "@/Common/Components/Inputs/InlineOptionsControl";
import { RadioButtonv2 } from "@/Common/Components/Inputs/RadioButtonv2";
import { Locale } from "@/Common/Models/Localization/Locale";
import { Patient } from "@/Common/Models/Patient/Patient";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { ObstetricalFileField, ObstetricalFileRedirectField } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/ObstetricalFileField";
import { ObstetricalFileControlRenderer } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRenderer";
import { ClickableTextRenderOptions } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRendererOptions";
import { BritishColumbiaObstetricalFileFieldLimits } from "@/Patient/Components/MedicalNote/ObstetricalFile/Form/BritishColumbia/BritishColumbiaObstetricalFileFieldLimits";
import { ObstetricsCalculator } from "@/Patient/Helpers/ObstetricsCalculator";
import { BritishColumbiaObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/BritishColumbia/BritishColumbiaObstetricalFileForm";
import { CareProviderRole } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part1/CareProviderRole";
import { ObstetricalFileMedicalSummary } from "@/Patient/Models/ObstetricalFile/ObstetricalFileMedicalSummary";
import { BaseObstetricalFileSubSection, BaseObstetricalFileSubSectionProps } from "../../Common/Subcomponent/BaseObstetricalFileSubSection";
import { AdditionalTextboxOptions, BooleanRadioButtonsWithTextboxRenderOptions, InlineTexboxOptions } from "./BritishColumbiaObstetricalFileComponentOptions";

interface BaseBritishColumbiaObstetricalFileSubSectionProps extends BaseObstetricalFileSubSectionProps<BritishColumbiaObstetricalFileForm> {
    patient: Patient;
    obstetricalFileMedicalSummary: ObstetricalFileMedicalSummary;
    consultationDateAtStartOfObstetricalFile: Date;
    renderObstetricalCounters: (colClassName: string) => JSX.Element;
    getClickableTextOptions: (id: string, context: string, leftAlign?: boolean) => ClickableTextRenderOptions;
}

abstract class BaseBritishColumbiaObstetricalFileSubSection<TProps extends BaseBritishColumbiaObstetricalFileSubSectionProps, TState> extends BaseObstetricalFileSubSection<BritishColumbiaObstetricalFileForm, TProps, TState> {
    protected controlRenderer: ObstetricalFileControlRenderer;
    protected get locale(): Locale { return Locale.en; }
    protected readonly page1FieldLimits = BritishColumbiaObstetricalFileFieldLimits.Page1;
    protected readonly page2FieldLimits = BritishColumbiaObstetricalFileFieldLimits.Page2;

    private BritishColumbiaAdditionalTextboxInputs: { [index: string]: HTMLInputElement } = {};

    public componentWillMount(): void {
        this.setAcceptableLocales([Locale.en]);
        super.componentWillMount();
    }

    protected renderRedirectField(label: string, field: React.ReactNode, onRedirect: () => void, className?: string): JSX.Element {
        return (
            <ObstetricalFileRedirectField
                label={label}
                field={field}
                className={className}
                locale={this.locale}
                onClick={() => onRedirect()}
            />
        );
    }

    protected renderTitle(sectionNumber: number = null, titleLabel?: string, className: string = "", style?: React.CSSProperties): JSX.Element {
        const number = sectionNumber ? `${sectionNumber}. ` : "";
        return <h4 className={`section-title ${className}`} style={style}>{number}{titleLabel && this.getString(titleLabel)}</h4>;
    }

    protected getTitleNumberAsLabelPrefix(titleNumber: number): React.ReactNode {
        return <span className="number-label-prefix">{titleNumber}.</span>;
    }

    protected getLabelSuffix(label: string, className: string = "", style: React.CSSProperties = {}): React.ReactNode {
        return <span className={`label-suffix ${className}`} style={style}>{label}</span>;
    }

    protected renderMultipleLabel(label: string, prefixLabel?: React.ReactNode, suffixLabel?: React.ReactNode, outerClassname: string = ""): React.ReactNode {
        return (
            <div className={`flex-display flex-space-between ${outerClassname}`}>
                <span className="field-label label-with-prefix flex-col-gap-xs">
                    {prefixLabel ?? ""}
                    {this.getString(label)}
                    {suffixLabel ?? ""}
                </span>
            </div>
        );
    }

    protected renderHeader(label: string, number: number): JSX.Element {
        return (
            <div className="col-md-12">
                {this.renderTitle(number, label, "", { marginBottom: "0px" })}
            </div>
        );
    }

    protected renderAllEmptyFieldAsNoLink(onSelect: () => void): JSX.Element {
        return (
            <div className="col-md-12">
                {!this.props.readonly && (
                    <p>
                        <a onClick={() => onSelect()} tabIndex={0} onKeyUp={e => this.onEnterKeyUp(e, onSelect)}>
                            {this.getString("ObstetricalFile_MarkAllEmptyFieldsAsNo")}
                        </a>
                    </p>
                )}
            </div>
        );
    }

    protected getFalseTrueRadioOptionsWithoutLabel(): InlineOption<boolean>[] {
        return [
            new InlineOption<boolean>(false, null),
            new InlineOption<boolean>(true, null)
        ];
    }

    protected renderNoYesTitleSection5To9(): JSX.Element {
        return (
            <div className="row">
                <div className="col-md-12 margin-sm-top">
                    <div className="display-inline-flex">
                        <span className="text-bold">{this.getString("NoLabel").substr(0, 1)}</span>
                        <span className="text-bold margin-lg-left">{this.getString("YesLabel").substr(0, 1)}</span>
                        {this.getLabelSuffix(`(${this.getString("SpecifyLowerCaseLabel")})`, "extra-info-label margin-sm-left")}
                    </div>
                </div>
            </div>
        );
    }

    protected renderRadioCheckboxWithLabel(name: string, label: string, value: boolean, onChange: (value: boolean) => void, additional?: React.ReactNode, isCheckbox: boolean = false, outerWrapperClassName?: string): JSX.Element {
        return (
            <div className={`${outerWrapperClassName ?? "padding-xl-left margin-lg-left"}`}>
                <div className="flex-row flex-space-between flex-align-items-center padding-xs-top padding-xs-bottom">
                    <div className="flex-row">
                        {this.controlRenderer.renderRadioButtonWithLabel(
                            `${name}-bc-antenatal-id`,
                            name,
                            this.getString(label),
                            value,
                            this.props.readonly,
                            () => onChange(!value),
                            false,
                            "",
                            "margin-none-bottom margin-xs-left",
                            isCheckbox
                        )}
                    </div>
                    {additional}
                </div>
            </div>
        );
    }

    protected getAdditionalTextBox(options: AdditionalTextboxOptions): React.ReactNode {
        const displayClassName = options.flexDisplay ? "flex-display" : "display-inline-flex";

        return (
            <div className={`${options.label ? `${displayClassName} flex-align-items-center` : ""} flex-justify-end width-100`} style={options.containerStyle}>
                {options.label && <label htmlFor={options.id} className="initial-width text-right padding-xs-right">{options.label}</label>}
                <div className={`${options.valueClassName ?? ""} ${this.props.readonly ? "text-right" : ""}`} style={options.valueStyle}>
                    {this.props.readonly
                        ? <span className="field-text margin-sm-left">{options.value}</span>
                        : (
                            <input
                                className="form-control input line-height-normal"
                                id={options.id}
                                type={options.isNumeric ? "number" : "text"}
                                min={options.min}
                                value={options.value}
                                onChange={(e: any) => {
                                    if (!options.isNumeric) {
                                        options.onChange(e.target.value);
                                        return;
                                    }

                                    const value: string = e.target.value;
                                    options.onChange(value?.substring(0, options.maxLength) ?? "");
                                }}
                                onBlur={this.props.handleFormUpdate}
                                maxLength={options.maxLength}
                                ref={inputRef => this.BritishColumbiaAdditionalTextboxInputs[options.id] = inputRef}
                            />
                        )
                    }
                </div>
            </div>
        );
    }

    protected renderRadioButtonsWithTextBox(options: BooleanRadioButtonsWithTextboxRenderOptions): JSX.Element {
        return (
            <div className="col-md-12 inline-radio-textbox inline-selection-justify-between">
                {this.renderBooleanRadioButtonsBeforeTextbox(options)}
                <label className="margin-none margin-xs-right" style={{ width: "auto", minWidth: "fit-content" }}>{this.getString(options.label)}</label>
                <div className={`${options.textBoxClassName ?? "width-100"}  padding-none-right padding-none-left`}>
                    {this.getAdditionalTextBox({
                        id: options.id,
                        value: options.value.Description,
                        onChange: value => options.onChangeDescription(value),
                        maxLength: options.maxLength
                    })}
                </div>
            </div>
        );
    }

    protected renderInlineTextbox(options: InlineTexboxOptions, inputFieldStyles: React.CSSProperties = {}): JSX.Element {
        return (
            <ObstetricalFileField
                label={options.label}
                labelClassName={`margin-sm-right ${options.labelClassName ?? ""}`}
                field={this.props.readonly
                    ? <span className="field-text">{options.value}</span>
                    : (
                        <div className={options.setInputFullWidth ? "width-100" : ""}>
                            <input
                                style={inputFieldStyles}
                                className="form-control input line-height-normal"
                                id={options.id}
                                value={options.value}
                                onChange={(e: any) => {
                                    let value: string = e.target.value;
                                    if (options.isNumeric && options.maxLength > 0)
                                        value = value.substring(0, options.maxLength);

                                    options.onChange(value);
                                }}
                                onBlur={() => this.props.handleFormUpdate()}
                                type={options.isNumeric ? "number" : "text"}
                                maxLength={options.maxLength}
                                ref={inputRef => this.BritishColumbiaAdditionalTextboxInputs[options.id] = inputRef}
                            />
                        </div>
                    )
                }
                className={options.className}
                locale={this.locale}
                inlineFlex={true}
                labelSuffix={options.labelSuffix}
                labelStyle={options.labelStyle}
            />
        );
    }

    protected renderInlineDatePicker(label: string, value: Date, onChange: (value: Date) => void, className: string = "", labelClassName: string = "", ref?: React.Ref<DateSelector>): JSX.Element {
        return (
            <ObstetricalFileField
                label={label}
                labelClassName={`margin-sm-right ${labelClassName}`}
                field={this.props.readonly
                    ? <span className="field-text margin-sm-left">{LuxonDateTimeParser.parse(value)?.toFormat("yyyy-MM-dd") ?? ""}</span>
                    : (
                        <div className="width-100">
                            {this.controlRenderer.renderDateSelector({
                                value: value,
                                onChange: onChange,
                                width: 120,
                                ref: ref
                            })}
                        </div>
                    )
                }
                className={className}
                locale={this.locale}
                inlineFlex={true}
            />
        );
    }

    protected renderBooleanRadioButtonsBeforeTextbox(options: BooleanRadioButtonsWithTextboxRenderOptions): JSX.Element {
        return (
            <div className={`${options.radioOuterWrapperClassName ?? "radio-before-textbox"} padding-none-left`}>
                {this.controlRenderer.renderInlineRadio({
                    value: options.value.Value,
                    items: this.getFalseTrueRadioOptionsWithoutLabel(),
                    onChange: value => {
                        options.onChangeBoolean(value);
                        this.setFocusTextbox(value, options.id);
                    },
                    id: options.id + "_text-field",
                    className: options.radioClassName ?? "radios-one-label",
                    additionalWrapperClassName: options.radioWrapperClassName,
                    optionSpacing: "15px"
                })}
            </div>
        );
    }

    protected renderRadioButtonGroup<TKey>(name: string, value: string, onChange: (key: TKey) => void, items: KeyValuePair<TKey, string>[]): JSX.Element {
        return (
            <RadioButtonv2
                wrapperClassName="display-contents"
                cssClass="margin-xs-between-radios"
                items={items.toImmutable()}
                htmlName={name}
                onChangeCallback={item => onChange(item.Key)}
                selectedItem={value}
                disabled={this.props.readonly}
            />
        );
    }

    protected setFocusTextbox(isActive: boolean, id: string): void {
        if (isActive && this.BritishColumbiaAdditionalTextboxInputs?.[id]) {
            this.BritishColumbiaAdditionalTextboxInputs[id].focus();
            this.BritishColumbiaAdditionalTextboxInputs[id].select();
        }
    }

    protected mutateModelStateThenFocusTextbox(mutation: (s: BritishColumbiaObstetricalFileForm) => void, focus: boolean, id: string, updateForm: boolean = true): void {
        this.props.mutateModelState(
            s => mutation(s),
            () => {
                if (updateForm)
                    this.props.handleFormUpdate();

                this.setFocusTextbox(focus, id);
            }
        );
    }

    protected getCareProviderRoleOptions(): InlineOption<CareProviderRole>[] {
        return [
            new InlineOption<CareProviderRole>(CareProviderRole.Doctor, this.getString("DoctorAbbrevLabel")),
            new InlineOption<CareProviderRole>(CareProviderRole.RegisteredMidwife, this.getString("RegisteredMidwifeAbbrevLabel")),
            new InlineOption<CareProviderRole>(CareProviderRole.NursePractitioner, this.getString("NursePractitionerAbbrevLabel"))
        ];
    }

    protected setGestationalAgeSection14(form: BritishColumbiaObstetricalFileForm): void {
        const estimateDateOfBirth = form.Section12.ConfirmedEstimatedDateOfDelivery
            ?? form.Section3.EstimatedDateOfBirthByUltrasound
            ?? form.Section3.EstimatedDateOfBirthByLastMenstrualPeriod;

        if (estimateDateOfBirth)
            form.Section14.GestationalAge = ObstetricsCalculator.calculateWeeksOfGestation(estimateDateOfBirth, form.Section14.Date);
    }

    private onEnterKeyUp(e: React.KeyboardEvent, onSelect: () => void): void {
        if (e.keyCode === 13) {
            onSelect();
        }
    }
}

export { BaseBritishColumbiaObstetricalFileSubSection, BaseBritishColumbiaObstetricalFileSubSectionProps };
